import { LoginError } from '../services/login.service';
import { createReducer } from '@ppl/store';
import type { Actions } from '../../../core/store/core.actions';
import type { TwoFAMethod, TwoFAType } from '../../../shared/modules/two-factor/domain/two-factor';


export interface LoginUser {
  name: string | null;
  email: string;
  idp_id: number | null;
  two_fa_methods: TwoFAMethod[];
  required_two_fa_methods: TwoFAType[] | null;
  logged_in: boolean;
}

export interface User {
  id: number;
  first_name: string;
  last_name: string;
  company: string;
  email: string;
  partner: string;
  phone: string;
  picture_url: string;
  idp_id: number | null;
  lang: string;
}

export interface WelcomeScreenState {
  can_change_password: boolean;
  must_change_password: boolean;
}

export interface EmailVerificationScreenState {
  email_verified: boolean;
}

const defaultState: LoginState = {
  validating: false,
  error: LoginError.NO_ERROR,
  error_str: null,
  jwt: null,
  ui: 'default',
  teamspace_id: null,
  welcomeScreen: {
    can_change_password: false,
    must_change_password: false,
  },
  emailVerificationScreen: {
    email_verified: false
  },
  mobileSignInEnabled: false,
  loginUser: {
    name: null,
    email: null,
    idp_id: undefined,
    two_fa_methods: [],
    required_two_fa_methods: null,
    logged_in: undefined
  },
  user: {
    id: -1,
    first_name: '',
    last_name: '',
    company: '',
    email: '',
    partner: '',
    phone: '',
    picture_url: '',
    idp_id: -1,
    lang: ''
  },
  extra: null
};

export const loginReducer = createReducer<LoginState, Actions>({

  Login_Validating(state, { payload }) {
    return {
      ...state,
      validating: payload
    };
  },

  Login_SetError(state, { payload }) {
    return {
      ...state,
      error: payload.error,
      error_str: payload.error_str
    };
  },

  Login_InitState(state, { payload }) {
    return {
      ...state,
      ...payload
    };
  },

  Login_SetLoginUser(state, { payload }) {
    return {
      ...state,
      loginUser: {
        ...state.loginUser,
        ...payload
      }
    };
  },

  Login_SetLoginUserEmail(state, { payload }) {
    return {
      ...state,
      loginUser: {
        ...state.loginUser,
        email: payload
      }
    };
  },

  Login_SetLoginTwoFAMethods(state, { payload }) {
    return {
      ...state,
      loginUser: {
        ...state.loginUser,
        two_fa_methods: payload
      }
    };
  },

  Login_SetLoginRequiredTwoFAMethods(state, { payload }) {
    return {
      ...state,
      loginUser: {
        ...state.loginUser,
        required_two_fa_methods: payload
      }
    };
  },

  Login_SetLoginUserIDP(state, { payload }) {
    return {
      ...state,
      loginUser: {
        ...state.loginUser,
        idp_id: payload
      }
    };
  },

  Login_SetLoginUserIsLoggedIn(state, { payload }) {
    return {
      ...state,
      loginUser: {
        ...state.loginUser,
        logged_in: payload
      }
    };
  },

  Login_SetUser(state, { payload }) {
    return {
      ...state,
      user: payload
    };
  }

}, defaultState);

export interface LoginState {
  validating: boolean;
  error: LoginError;
  error_str: string;
  jwt: string;
  welcomeScreen: WelcomeScreenState;
  emailVerificationScreen: EmailVerificationScreenState;
  mobileSignInEnabled: boolean;
  ui: 'default' | 'popup';
  teamspace_id: number | null;
  loginUser: LoginUser;
  user: User;
  extra: {
    description?: string,
    disable_google_sign_in?: boolean,
    disable_microsoft_sign_in?: boolean
  } | null;
}
